// Тип для настроек выравнивания.
export interface AlignmentTune {
  alignment: 'left' | 'center' | 'right';
}

export interface EditorJSBlock<T extends string = string, D = any> {
  type: T;
  data: D;
  tunes?: {
    anyTuneName?: AlignmentTune;
  };
}

export interface EditorJSOutput {
  time?: number;
  blocks: EditorJSBlock[];
  version?: string;
}

// Типы данных для всех блоков
export interface HeaderBlockData {
  text: string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  alignment?: 'left' | 'center' | 'right';
}

export interface ParagraphBlockData {
  text: string;
  alignment?: 'left' | 'center' | 'right';
}

// Стандартная форма ListBlockData (берётся из документации Editor.js и примеров):
export interface SimpleListBlockData {
  style: 'ordered' | 'unordered';
  items: string[];
  meta?: Record<string, any>;
}

// Расширяем, чтобы понять вложенные списки (есть реализации Editor.js, которые
// в items вместо строки могут отдавать объект { content, meta, items[] }):
export interface NestedListItem {
  content: string;
  meta?: Record<string, any>;
  items?: Array<NestedListItem | string>;
}

export interface NestedListBlockData {
  style: 'ordered' | 'unordered';
  items: Array<NestedListItem | string>;
  meta?: Record<string, any>;
}

// Объединённый тип — либо простая форма, либо вложенная:
export type ListBlockData = SimpleListBlockData | NestedListBlockData;

export interface QuoteBlockData {
  text: string;
  caption?: string;
  alignment?: 'left' | 'center' | 'right';
}

export interface ImageBlockData {
  file?: {
    url: string;
  };
  url: string;
  caption: string;
  withBorder?: boolean;
  stretched?: boolean;
  withBackground?: boolean;
}

export interface EmbedBlockData {
  service: 'youtube' | 'vimeo' | 'coub' | 'gfycat' | string;
  source: string;
  embed: string;
  width?: number;
  height?: number;
  caption?: string;
}

export interface AttachesBlockData {
  file: {
    url: string;
    name: string;
    extension: string;
    size: number;
  };
  title: string;
}

export interface TableBlockData {
  content: string[][];
}

export interface WarningBlockData {
  title: string;
  message: string;
}

export interface AlertBlockData {
  type: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger' | 'light';
  message: string;
}

export interface DelimiterBlockData {
}

// Расширяемый тип для всех блоков
export type EditorJSDataTypes = {
  header: HeaderBlockData;
  paragraph: ParagraphBlockData;
  list: ListBlockData;
  quote: QuoteBlockData;
  image: ImageBlockData;
  embed: EmbedBlockData;
  attaches: AttachesBlockData;
  table: TableBlockData;
  warning: WarningBlockData;
  alert: AlertBlockData;
  delimiter: DelimiterBlockData;
};

// Тип для настроек выравнивания
export type AlignmentType = 'left' | 'center' | 'right';

// Функция для проверки структуры данных
export function isEditorJSOutput(data: any): data is EditorJSOutput {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray(data.blocks) &&
    data.blocks.every((block: any) =>
      typeof block === 'object' &&
      block !== null &&
      typeof block.type === 'string' &&
      typeof block.data === 'object'
    )
  );
}

// Функция для безопасного парсинга
export function parseEditorJSData(jsonString: string): EditorJSOutput {
  try {
    const parsed = JSON.parse(jsonString);
    if (!isEditorJSOutput(parsed)) {
      throw new Error('Invalid EditorJS data structure');
    }
    return parsed;
  } catch (error) {
    console.error('Failed to parse EditorJS data:', error);
    // Возвращаем пустую структуру в случае ошибки
    return {
      blocks: [],
      time: Date.now(),
      version: '2.0'
    };
  }
}
